import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import defaultCoverWhite from 'assets/images/logos/defaultCoverWhite.png';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';

function SignUp() {
  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const openPage = (route) => {
    navigate(route);
  };
  const url = new URL(window.location.href);
  const queryString = url.search;
  const params = new URLSearchParams(queryString);
  const roleName = params.get('name') || '';
  let orgName = params.get('orgName') || '';
  let isEmailVerified = false;
  let isAdmin = false;
  let token = null;
  [orgName, token] = orgName?.split('?token=');

  const onSubmit = async (values, { resetForm }) => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('firstName', values.firstName);
      formData.append('lastName', values.lastName);
      formData.append('email', values.email);
      formData.append('phone', values.phone);
      formData.append('country', values.country);
      formData.append('city', values.city);
      formData.append('password', values.password);
      formData.append('logo', values.logo);

      const response = await axios.post(`${apiUrl}auth-service/auth`, formData,
        {
          headers: { 'Content-Type': `multipart/form-data` }
        });
      if (response.data?.success) {
        toast.success(response.data?.message || 'Successfully signed up!');
        resetForm();
        setTimeout(() => {
          navigate(-1);
          setIsLoading(false);
        }, 1500);
      } else {
        throw new Error('Failed to sign up');
      }
    } catch (error) {
      console.error(error.response.data?.message || error.message);
      toast.error(error.response.data?.message || error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const newUser = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      country: '',
      city: '',
      logo: '',
    },
    validationSchema: Yup.object({
      firstName: Yup.string()
        .required('Required'),
      lastName: Yup.string()
        .required('Required'),
      phone: Yup.string()
        .required('Required')
        .matches(/^\+?[1-9]\d{1,14}$/, 'Mobile number is not valid')
        .min(10, 'Not a valid phone number')
        .max(15, 'Not a valid phone number'),
      email: Yup.string()
        .required('Required')
        .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Enter a valid email')
        .email('Enter a valid email'),
      password: Yup.string()
        .required('Required')
        .min(8, 'Password must be at least 8 characters long'),
    }),
    onSubmit,
  });

  return (
    <div className="flex flex-col md:flex-row h-full min-h-screen w-full">
      {/* Left section */}
      <div className="w-full md:w-2/3 bg-slate-800 p-10 flex flex-col justify-center text-white">
        <div className="max-w-md mx-auto">
          <h1 className="text-3xl font-bold mb-4">Manage Your Events</h1>
          <p className="mb-4 text-base">Easily manage, track, and organize all your events from one powerful dashboard. Streamline your workflow and make event planning simpler, smarter, and more efficient.</p>
        </div>
      </div>

      {/* Right section */}
      <div className="w-full md:w-1/3 bg-white p-8 flex flex-col justify-center">
        <div className="max-w-md mx-auto w-full">
          {/* Logo */}
          <div className="flex justify-center mb-4">
            <img src={defaultCoverWhite} alt="Logo" className="h-16" />
          </div>

          <h2 className="text-xl font-semibold text-center mb-6 text-slate-800">Create an Account</h2>

          <form onSubmit={newUser.handleSubmit} className="space-y-4">
            {[
              { name: 'firstName', type: 'text', label: 'First Name' },
              { name: 'lastName', type: 'text', label: 'Last Name' },
              { name: 'phone', type: 'tel', label: 'Phone', placeholder: 'e.g. +1234567890' },
              { name: 'email', type: 'email', label: 'Email' },
              { name: 'password', type: 'password', label: 'Password' },
              // { name: 'country', type: 'text', label: 'Country' },
              // { name: 'city', type: 'text', label: 'City' },
            ].map(({ name, type, label, placeholder }) => (
              <div key={name}>
                <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                <input
                  type={type}
                  name={name}
                  placeholder={placeholder || label}
                  value={newUser.values[name]}
                  onChange={newUser.handleChange}
                  onBlur={newUser.handleBlur}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {newUser.touched[name] && newUser.errors[name] && (
                  <p className="text-sm text-red-500 mt-1">{newUser.errors[name]}</p>
                )}
              </div>
            ))}

            <button
              type="submit"
              className="w-full bg-blue-600 text-white py-2 rounded-md hover:bg-blue-500 transition duration-200 text-sm"
              disabled={isLoading}
            >
              {isLoading ? 'Creating...' : 'Sign Up'}
            </button>
          </form>
        </div>
      </div>

      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );

}

export default SignUp;
